import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, Trash2, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { attestationsAPI } from '@/utils/api';

const AttestationsPage = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file_url: '',
    report_date: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const data = await attestationsAPI.getAll();
      setReports(data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await attestationsAPI.create(formData);
      setFormData({
        title: '',
        description: '',
        file_url: '',
        report_date: ''
      });
      setShowUploadForm(false);
      fetchReports();
    } catch (error) {
      console.error('Error creating report:', error);
      alert('Failed to add report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      await attestationsAPI.delete(reportId);
      fetchReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Failed to delete report. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const [year, month] = dateString.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="attestations-page">
      {/* Hero Section */}
      <section className="bg-kitco-navy text-white py-20" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-4" data-testid="hero-title">{t('attestations.hero.title')}</h1>
          <p className="text-xl text-grey-300 max-w-3xl" data-testid="hero-subtitle">
            {t('attestations.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20" data-testid="content-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Info Card */}
          <Card className="mb-12 bg-amber-50 border-amber-200" data-testid="info-card">
            <CardContent className="p-6">
              <p className="text-grey-600 leading-relaxed" data-testid="info-text">
                Kitco recognizes the need for a high standard of public transparency for consumers and businesses to build trust. 
                As a result, we have engaged Cohen & Co., a nationally ranked accounting firm, to conduct monthly attestations 
                on our Management Report. Every attestation that has been published since the launch of DirectReserve™ is available below.
              </p>
            </CardContent>
          </Card>

          {/* Admin Upload Button */}
          <div className="mb-8 flex justify-end">
            <Button
              onClick={() => setShowUploadForm(!showUploadForm)}
              className="bg-kitco-gold hover:bg-kitco-gold-hover text-black font-semibold"
              data-testid="toggle-upload-button"
            >
              <Upload className="mr-2 h-4 w-4" />
              {showUploadForm ? 'Cancel' : 'Add Report'}
            </Button>
          </div>

          {/* Upload Form */}
          {showUploadForm && (
            <Card className="mb-12" data-testid="upload-form-card">
              <CardHeader>
                <CardTitle data-testid="upload-form-title">Add New Attestation Report</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="upload-form">
                  <div>
                    <Label htmlFor="title" data-testid="title-label">Report Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="e.g., DirectReserve Attestation Report"
                      required
                      className="mt-1"
                      data-testid="title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="report_date" data-testid="date-label">Report Date (YYYY-MM) *</Label>
                    <Input
                      id="report_date"
                      name="report_date"
                      value={formData.report_date}
                      onChange={handleChange}
                      placeholder="2025-01"
                      pattern="\d{4}-\d{2}"
                      required
                      className="mt-1"
                      data-testid="date-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="file_url" data-testid="url-label">File URL *</Label>
                    <Input
                      id="file_url"
                      name="file_url"
                      type="url"
                      value={formData.file_url}
                      onChange={handleChange}
                      placeholder="https://example.com/report.pdf"
                      required
                      className="mt-1"
                      data-testid="url-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description" data-testid="description-label">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Optional description or notes about this report"
                      rows={3}
                      className="mt-1"
                      data-testid="description-textarea"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-kitco-gold hover:bg-kitco-gold-hover text-black font-semibold"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Report'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Reports List */}
          <div className="space-y-6" data-testid="reports-list">
            {loading ? (
              <div className="text-center py-12" data-testid="loading-state">
                <p className="text-grey-400">Loading reports...</p>
              </div>
            ) : reports.length === 0 ? (
              <Card data-testid="empty-state">
                <CardContent className="p-12 text-center">
                  <FileText className="h-16 w-16 text-grey-300 mx-auto mb-4" />
                  <p className="text-grey-400 text-lg">{t('attestations.comingSoon')}</p>
                </CardContent>
              </Card>
            ) : (
              reports.map((report, index) => (
                <Card key={report.id} className="hover:shadow-lg transition-shadow" data-testid={`report-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start flex-1">
                        <FileText className="h-6 w-6 text-kitco-gold mr-4 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-kitco-navy mb-2" data-testid={`report-title-${index}`}>
                            {report.title}
                          </h3>
                          <div className="flex items-center text-grey-500 mb-2">
                            <Calendar className="h-4 w-4 mr-2" />
                            <span data-testid={`report-date-${index}`}>{formatDate(report.report_date)}</span>
                          </div>
                          {report.description && (
                            <p className="text-grey-500 mt-2" data-testid={`report-description-${index}`}>
                              {report.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <a
                          href={report.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`download-button-${index}`}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            className="hover:bg-amber-50"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </a>
                        <Button
                          variant="outline"
                          size="sm"
                          className="hover:bg-red-50 text-red-600 border-red-300"
                          onClick={() => handleDelete(report.id)}
                          data-testid={`delete-button-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AttestationsPage;
